/* ============================================================
   Per-peptide dosing-chart builder (P2 long-tail pages).
   Produces a reconstitution matrix for one peptide using the SAME math as the
   live calculator (src/lib/peptide.js) so the printed table and the interactive
   tool never disagree. Rows = bacteriostatic water added; columns = target dose;
   cell = insulin-syringe units to draw on a U-100 syringe.
   ============================================================ */
import { calculate } from './peptide.js'
import { PEPTIDES } from '../data/peptides.js'

// High-intent peptides listed first — drives ordering on the /charts index and
// the "other charts" lists.
const PRIORITY = [
  'bpc-157', 'tb-500',
  'semaglutide', 'tirzepatide', 'retatrutide', 'cagrilintide',
  'ipamorelin', 'cjc-1295-no-dac', 'cjc-1295-dac', 'sermorelin', 'tesamorelin',
  'ghrp-2', 'ghrp-6', 'hexarelin',
  'ghk-cu', 'mots-c', 'aod-9604', 'pt-141', 'melanotan-2', 'epithalon',
  'dsip', 'melanotan-1', 'thymosin-alpha-1', 'igf-1-lr3', 'follistatin-344',
  'kisspeptin-10', 'gonadorelin', 'ss-31', 'kpv', 'll-37',
  'adipotide', 'humanin', 'pinealon', 'thymulin', 'survodutide',
]

// EVERY peptide with a calcPreset gets a dosing-chart page (reconstitution table
// + live calculator). Priority names first, then any remaining injectables — so a
// new library entry with a preset automatically gets a chart. Each chart links
// back to the calculator, the peptide profile, the dosage guide, and other
// charts: dense internal linking that funnels crawl equity toward the calculator.
const PRESET_SLUGS = PEPTIDES.filter((p) => p && p.calcPreset).map((p) => p.slug)
export const CHART_SLUGS = [
  ...PRIORITY.filter((s) => PRESET_SLUGS.includes(s)),
  ...PRESET_SLUGS.filter((s) => !PRIORITY.includes(s)),
]

// Bacteriostatic-water volumes people actually add (mL) → chart rows.
const WATER_ML = [1, 2, 3, 5]

// Round a dose to a "clean" value so the derived columns read naturally.
function niceDose(v, unit) {
  if (!(v > 0)) return 0
  if (unit === 'mg') {
    if (v >= 1) return Math.round(v * 4) / 4 // nearest 0.25 mg
    return Math.round(v * 100) / 100 // nearest 0.01 mg
  }
  // mcg
  if (v >= 500) return Math.round(v / 100) * 100
  if (v >= 100) return Math.round(v / 25) * 25
  if (v >= 50) return Math.round(v / 10) * 10
  return Math.round(v / 5) * 5
}

// Derive a small set of realistic doses centred on the peptide's typical dose.
function doseColumns({ dose, doseUnit }) {
  const mults = [0.5, 1, 1.5, 2]
  const out = []
  for (const m of mults) {
    const d = niceDose(dose * m, doseUnit)
    if (d > 0 && !out.includes(d)) out.push(d)
  }
  return out.length ? out : [dose]
}

/**
 * Build the chart model for a peptide (expects a `calcPreset`).
 * @returns { vialMg, doseUnit, doses, rows } or null
 */
export function buildChart(peptide) {
  const cp = peptide?.calcPreset
  if (!cp) return null
  const { vialMg, doseUnit = 'mcg' } = cp
  const doses = doseColumns(cp)

  const rows = WATER_ML.map((waterMl) => {
    const cells = doses.map((dose) => {
      const r = calculate({ vialMg, waterMl, dose, doseUnit, syringe: 's100' })
      return {
        dose,
        units: r.ok ? r.units : null,
        overCap: r.ok ? r.units > 100 : false, // needs >1 mL syringe
      }
    })
    // concentration is dose-independent — take it from any successful cell calc
    const c = calculate({ vialMg, waterMl, dose: doses[0], doseUnit, syringe: 's100' })
    return {
      waterMl,
      concMgMl: c.ok ? c.concentrationMgMl : null,
      mcgPerUnit: c.ok ? c.mcgPerUnit : null,
      cells,
    }
  })

  return { vialMg, doseUnit, doses, rows, preset: cp }
}
